import React, { useState, useEffect, useCallback } from 'react';

const API = import.meta.env.VITE_API_BASE_URL || '';

function UnitDiffRow({ label, before, after }) {
  const changed = before !== after;
  return (
    <div className="flex items-center justify-between text-xs py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className={changed ? 'font-bold text-yellow-300' : 'text-gray-300'}>
        {String(before)} {changed && <span className="text-gray-500">→</span>} {changed && String(after)}
      </span>
    </div>
  );
}

function AbilityPicker({ card, abilityIndex, onChange }) {
  if (!card?.abilities?.length) return <div className="text-red-400 text-xs">Nessuna ability compilata per questa carta.</div>;
  return (
    <select
      value={abilityIndex}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
    >
      {card.abilities.map((ab, i) => (
        <option key={i} value={i}>
          [{i}] {ab.trigger} → {(Array.isArray(ab.effects) ? ab.effects : ab.effects ? [ab.effects] : []).map((e) => e.type).join(', ') || '—'}
        </option>
      ))}
    </select>
  );
}

export default function CardSandbox() {
  const [search, setSearch] = useState('');
  const [allCards, setAllCards] = useState([]); // tutte le 1048, per lookup + ricerca libera
  const [triage, setTriage] = useState({ total: 0, items: [], stale: false, note: null });
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const [abilityIndex, setAbilityIndex] = useState(0);
  const [targetMight, setTargetMight] = useState(5);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState({ confirmedCount: 0, total: 0, items: [] });

  const fetchAllCards = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/card-debug`);
      const data = await res.json();
      setAllCards(data.cards || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchTriage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/card-qa/triage`);
      const data = await res.json();
      setTriage(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/card-qa/status`);
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchAllCards(); fetchTriage(); fetchStatus(); }, [fetchAllCards, fetchTriage, fetchStatus]);

  const cardById = new Map(allCards.map((c) => [c.id, c]));
  const reasonsById = new Map(triage.items.map((t) => [t.id, t.reasons]));

  const cards = showAll
    ? allCards.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.id.toLowerCase().includes(search.toLowerCase()))
    : triage.items.map((t) => cardById.get(t.id) || { id: t.id, name: t.name, abilities: [], text: '' });

  const selectCard = (card) => {
    setSelected(card);
    setAbilityIndex(0);
    setResult(null);
    setError(null);
    setNote('');
  };

  const statusFor = (cardId) => status.items.find((i) => i.card_id === cardId);

  const applyEffect = async () => {
    if (!selected) return;
    setRunning(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/card-qa/test-ability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId: selected.id, abilityIndex, scenario: { targetMight } }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'errore sconosciuto');
      setResult(data);
    } catch (e) {
      setError(e.message);
      setResult(null);
    } finally {
      setRunning(false);
    }
  };

  const confirm = async (confirmed) => {
    if (!selected) return;
    await fetch(`${API}/api/card-qa/status/${encodeURIComponent(selected.id)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmed, note }),
    });
    fetchStatus();
  };

  const before = result?.targetBefore;
  const after = result?.targetAfter;
  const currentStatus = selected && statusFor(selected.id);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Card Sandbox</h1>
          <div className="text-gray-500 text-xs">
            {showAll
              ? "Ricerca libera su tutte le 1048 carte — per spot-check ad-hoc."
              : "Solo le carte segnalate da card-verifier.js / audit-card-effects.js, non tutte le 1048."}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm font-bold text-green-300">
            {showAll ? `${status.confirmedCount} / ${status.total} confermate` : `${status.confirmedCount} / ${triage.total} da rivedere`}
          </div>
          <button
            onClick={() => setShowAll((v) => !v)}
            className={`px-3 py-1 rounded border text-xs font-bold transition-colors ${
              showAll ? 'border-blue-500 bg-blue-950/40 text-blue-300' : 'border-gray-700 text-gray-400 hover:text-gray-200'
            }`}
          >
            {showAll ? '← Torna alla coda' : 'Mostra tutte le 1048 carte'}
          </button>
        </div>
      </div>

      {triage.stale && (
        <div className="bg-yellow-900/40 border border-yellow-700 rounded px-3 py-2 text-yellow-300 text-xs">
          ⚠ {triage.note}
        </div>
      )}

      {showAll && (
        <input
          type="text"
          placeholder="Cerca per nome o ID carta…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
        />
      )}

      <div className="flex gap-4 flex-1 min-h-0" style={{ height: 'calc(100vh - 220px)' }}>
        {/* Lista carte */}
        <div className="w-72 shrink-0 overflow-y-auto flex flex-col gap-1 pr-1">
          {cards.map((card) => {
            const s = statusFor(card.id);
            const reasons = reasonsById.get(card.id);
            return (
              <button
                key={card.id}
                onClick={() => selectCard(card)}
                className={`w-full text-left rounded border px-3 py-2 transition-colors text-xs ${
                  selected?.id === card.id ? 'border-blue-500 bg-blue-950/40' : 'border-gray-800 bg-gray-900/40 hover:bg-gray-800/60'
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-medium text-white truncate">{card.name}</span>
                  {s?.confirmed && <span className="shrink-0 text-green-400 font-bold">✓</span>}
                </div>
                <div className="text-gray-600 truncate" style={{ fontSize: 9 }}>{card.id}</div>
                {!showAll && reasons?.length > 0 && (
                  <div className="text-orange-400 truncate mt-0.5" style={{ fontSize: 9 }}>{reasons[0]}</div>
                )}
              </button>
            );
          })}
          {cards.length === 0 && (
            <div className="text-gray-600 text-sm text-center py-8">
              {showAll ? 'Nessun risultato' : '✓ Nessuna carta da rivedere — coda vuota.'}
            </div>
          )}
        </div>

        {/* Pannello scenario + risultato */}
        <div className="flex-1 min-h-0 overflow-auto">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-gray-700 text-sm">
              ← Seleziona una carta da testare
            </div>
          ) : (
            <div className="rounded border border-gray-700 bg-gray-900/40 p-4 flex flex-col gap-4">
              <div>
                <div className="font-bold text-white text-sm">{selected.name}</div>
                <div className="text-gray-500 text-xs">{selected.id} · {selected.type || '?'}</div>
                {reasonsById.get(selected.id)?.length > 0 && (
                  <div className="bg-orange-950/40 border border-orange-800 rounded px-2 py-1 mt-2 text-orange-300 text-xs">
                    {reasonsById.get(selected.id).map((r, i) => <div key={i}>⚠ {r}</div>)}
                  </div>
                )}
                <div className="bg-gray-950/60 rounded p-2 mt-2 text-xs text-gray-300 whitespace-pre-line">
                  {selected.text || '— nessun testo —'}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-gray-500 uppercase tracking-wide mb-1" style={{ fontSize: 9 }}>Ability da eseguire</div>
                  <AbilityPicker card={selected} abilityIndex={abilityIndex} onChange={setAbilityIndex} />
                </div>
                <div>
                  <div className="text-gray-500 uppercase tracking-wide mb-1" style={{ fontSize: 9 }}>Might unità target</div>
                  <input
                    type="number"
                    value={targetMight}
                    onChange={(e) => setTargetMight(Number(e.target.value))}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
                  />
                </div>
              </div>

              <button
                onClick={applyEffect}
                disabled={running}
                className="self-start px-4 py-1.5 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-sm font-bold"
              >
                {running ? 'Eseguo…' : 'Applica effetto'}
              </button>

              {error && <div className="bg-red-900/40 border border-red-700 rounded px-3 py-2 text-red-300 text-xs">{error}</div>}

              {before && after && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded border border-gray-800 bg-gray-950/40 px-3 py-2">
                    <div className="text-gray-500 uppercase tracking-wide mb-1" style={{ fontSize: 9 }}>Prima</div>
                    <UnitDiffRow label="Might" before={before.might} after={before.might} />
                    <UnitDiffRow label="Danno" before={before.damage} after={before.damage} />
                    <UnitDiffRow label="Esausta" before={before.exhausted} after={before.exhausted} />
                  </div>
                  <div className="rounded border border-gray-800 bg-gray-950/40 px-3 py-2">
                    <div className="text-gray-500 uppercase tracking-wide mb-1" style={{ fontSize: 9 }}>Dopo</div>
                    <UnitDiffRow label="Might" before={before.might} after={after.might} />
                    <UnitDiffRow label="Danno" before={before.damage} after={after.damage} />
                    <UnitDiffRow label="Esausta" before={before.exhausted} after={after.exhausted} />
                  </div>
                </div>
              )}

              {result && (
                <div className="flex flex-col gap-2 border-t border-gray-800 pt-3">
                  <textarea
                    placeholder="Note (opzionale)…"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white placeholder-gray-600 resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => confirm(true)}
                      className="px-3 py-1.5 rounded bg-green-800 hover:bg-green-700 text-xs font-bold"
                    >
                      ✓ Confermo che è corretto
                    </button>
                    <button
                      onClick={() => confirm(false)}
                      className="px-3 py-1.5 rounded bg-red-900/70 hover:bg-red-800 text-red-300 text-xs font-bold"
                    >
                      ✗ Non corretto
                    </button>
                  </div>
                  {currentStatus && (
                    <div className="text-gray-600 text-xs">
                      Stato attuale: {currentStatus.confirmed ? '✓ confermata' : '✗ segnalata'} il {currentStatus.confirmed_at ? new Date(currentStatus.confirmed_at).toLocaleString() : '—'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
